import React from 'react';
import {
  Question, Serializer, SurveyModel,
} from 'survey-core';
import { ReactQuestionFactory, SurveyQuestionElementBase } from 'survey-react-ui';
import {
  Button, IconButton, InputAdornment, TextField, Tooltip,
} from '@mui/material';
import {
  Lock, Visibility, VisibilityOff,
} from '@mui/icons-material';
import i18n from '../../../i18n';

const ENCRYPTED_INPUT_QUESTION_TYPE = 'encrypted-input';

class EncryptedInputModel extends Question {
  savedEncryptedValue: unknown = undefined;

  getType () {
    return ENCRYPTED_INPUT_QUESTION_TYPE;
  }

  isEmpty (): boolean {
    return !this.value;
  }
}

Serializer.addClass(
  ENCRYPTED_INPUT_QUESTION_TYPE,
  [],
  function () {
    return new EncryptedInputModel('');
  },
  'question',
);

export const isEncryptedMarker = (value: unknown): boolean => {
  return (
    typeof value === 'object' &&
    value !== null &&
    '__encrypted' in value &&
    (value as Record<string, unknown>).__encrypted === true
  );
};

const MASKED_DISPLAY = '••••••••';

interface EncryptedInputQuestionState {
  showValue: boolean;
}

class EncryptedInputQuestion extends SurveyQuestionElementBase {
  state: EncryptedInputQuestionState = { showValue: false };

  get question (): EncryptedInputModel {
    return this.props.question;
  }

  get isStoredEncrypted (): boolean {
    return isEncryptedMarker(this.question.value);
  }

  get isEditingFromEncrypted (): boolean {
    return this.question.savedEncryptedValue !== undefined;
  }

  get value (): string {
    if (this.isStoredEncrypted) {
      return MASKED_DISPLAY;
    }
    return this.question.value ?? '';
  }

  handleChange (value: string) {
    this.question.value = value || null;
  }

  handleToggleVisibility () {
    this.setState((prev: EncryptedInputQuestionState) => ({
      showValue: !prev.showValue,
    }));
  }

  handleClearEncrypted () {
    this.question.savedEncryptedValue = this.question.value;
    this.question.value = null;
  }

  handleCancelEdit () {
    const saved = this.question.savedEncryptedValue;
    this.question.savedEncryptedValue = undefined;
    this.question.value = saved;
  }

  render () {
    const { showValue } = this.state;
    const editingFromEncrypted = this.isEditingFromEncrypted;
    const isLocked = this.isStoredEncrypted;
    const isReadOnly = this.question.isReadOnly;

    return (
      <TextField
        fullWidth
        disabled={isReadOnly || isLocked}
        slotProps={{
          input: {
            'aria-label': this.question.title || 'Encrypted input',
            endAdornment: (
              <InputAdornment position='end'>
                {isLocked
                  ? (
                    <>
                      <Tooltip title={i18n.t('components:survey-questions.encrypted-input.locked_tooltip')}>
                        <Lock
                          color='action'
                          data-testid='LockIcon'
                        />
                      </Tooltip>
                      {!isReadOnly && (
                        <Button
                          data-testid='encrypted-input-change'
                          size='small'
                          sx={{ ml: 1 }}
                          onClick={() => this.handleClearEncrypted()}
                        >
                          {i18n.t('components:survey-questions.encrypted-input.change_button')}
                        </Button>
                      )}
                    </>
                    )
                  : (
                    <>
                      <IconButton
                        aria-label={showValue ? 'Hide value' : 'Show value'}
                        edge='end'
                        type='button'
                        onClick={() => this.handleToggleVisibility()}
                      >
                        {showValue ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                      {editingFromEncrypted && !isReadOnly && (
                        <Button
                          data-testid='encrypted-input-cancel'
                          size='small'
                          sx={{ ml: 1 }}
                          onClick={() => this.handleCancelEdit()}
                        >
                          {i18n.t('components:survey-questions.encrypted-input.cancel_button')}
                        </Button>
                      )}
                    </>
                    )}
              </InputAdornment>
            ),
          },
        }}
        type={isLocked || !showValue ? 'password' : 'text'}
        value={this.value}
        variant='outlined'
        onChange={(e) => this.handleChange(e.target.value)}
      />
    );
  }
}

export const registerEncryptedInputQuestion = () => {
  ReactQuestionFactory.Instance.registerQuestion(
    ENCRYPTED_INPUT_QUESTION_TYPE,
    (props) => {
      return React.createElement(EncryptedInputQuestion, props);
    },
  );
};

export const commitEncryptedInputEdits = (survey: SurveyModel): void => {
  for (const question of survey.getAllQuestions()) {
    if (question.getType() !== ENCRYPTED_INPUT_QUESTION_TYPE) continue;
    const q = question as EncryptedInputModel;
    if (typeof q.value !== 'string') continue;
    // Placeholder shape — no `value` field since plaintext was never server-encrypted yet
    q.value = q.savedEncryptedValue ?? { __encrypted: true };
    q.savedEncryptedValue = undefined;
  }
};

export const filterEncryptedInputValues = (
  data: Record<string, unknown>,
  survey: SurveyModel,
): Record<string, unknown> => {
  const encryptedInputNames = new Set(
    survey.getAllQuestions()
      .filter((q) => q.getType() === ENCRYPTED_INPUT_QUESTION_TYPE)
      .map((q) => q.getValueName()),
  );

  const result: Record<string, unknown> = {};
  for (const [ key, value ] of Object.entries(data)) {
    if (encryptedInputNames.has(key) && typeof value !== 'string') continue;
    result[key] = value;
  }
  return result;
};
